import { createContext, useContext } from "react";

import { TEditor } from "./Editor";

export enum EditorAction {
  Create = "create",
  Edit = "edit",
}
export type TSubject = Record<string, any> | string;

type TCreateOperation = {
  action: EditorAction.Create;
  subject: null;
  isCreating: true;
  isEditing: false;
};

type TEditOperation<T extends TSubject> = {
  action: EditorAction.Edit;
  subject: T;
  isCreating: false;
  isEditing: true;
};

export const OperationFactory = {
  create(): TCreateOperation {
    return {
      action: EditorAction.Create,
      subject: null,
      isCreating: true,
      isEditing: false,
    };
  },
  edit<T extends TSubject>(subject: T): TEditOperation<T> {
    return {
      action: EditorAction.Edit,
      subject: subject,
      isCreating: false,
      isEditing: true,
    };
  },
};

export type TSubjectOperation<T extends TSubject> =
  | TCreateOperation
  | TEditOperation<T>;

export type TEditingContext<T extends TSubject> = TSubjectOperation<T> &
  Omit<TEditor<T>, "current">;

export const EditingContext = createContext<TEditingContext<TSubject> | null>(
  null
);

export const useEditingContext = <T extends TSubject>() => {
  const context = useContext(EditingContext);
  if (!context) {
    throw new Error("useEditingContext must be used within a EditingProvider");
  }

  return context as TEditingContext<T>;
};
